/**
 * Native uygulama webview'ı tespiti — tek kaynak.
 *
 * Native uygulamalar ve deep link'ler URL parametrelerini farklı case'lerle
 * gönderiyor: `renderType` / `rendertype` / `render-type`, değer olarak `webview` / `webView`,
 * token için `accessToken` / `accesstoken`. Bu yüzden tüm parametre okumaları
 * burada case-insensitive yapılır; kod tabanında webview kontrolü sadece bu
 * modülün metotlarıyla yapılmalı.
 *
 * Kaynak (`source`) olarak query string ('?a=b' ya da 'a=b'), URLSearchParams
 * veya express `req.query` benzeri obje verilebilir. Verilmezse client'ta
 * `window.location.search` okunur, SSR'da webview değil kabul edilir.
 */

export const RENDER_TYPE_PARAMS = [
  'rendertype',
  'render-type',
  'renderType',
];

/** @deprecated `RENDER_TYPE_PARAMS` dizisini kullanın. Geriye dönük uyumluluk için korundu. */
export const RENDER_TYPE_PARAM = RENDER_TYPE_PARAMS;

export const RENDER_TYPE_WEBVIEW = 'webview';

/**
 * Native app / deep link token parametreleri (case-insensitive eşleşir).
 * `token-check`, kupon emri (coupon-order) özelliğinin kullandığı token parametresidir
 * (bkz. `CouponOrderPage.js` — `cryp_usr` bu parametreden set edilir); render-type
 * gönderilmeyen embedded girişlerde webview modunun tespit edilebilmesi için buraya dahil edilmiştir.
 */
export const EMBEDDED_TOKEN_QUERY_KEYS = [
  'accesstoken',
  'refreshtoken',
  'token-check',
];

/**
 * SPA navigasyonunda URL query'si kaybolduğunda webview modunu hatırlamak için
 * kullanılan kalıcı flag (client bootstrap'ta set edilir).
 */
let _webviewModeFlag = false;

export const setWebviewModeFlag = value => {
  _webviewModeFlag = value;
};

export const getWebviewModeFlag = () => _webviewModeFlag;

const normalize = value => (
  typeof value === 'string' ? value.trim().toLowerCase() : null
);

const toEntries = source => {
  let target = source;

  if (target === undefined || target === null) {
    if (typeof window === 'undefined') return null;
    target = window.location?.search ?? '';
  }

  if (typeof URLSearchParams !== 'undefined' && target instanceof URLSearchParams) {
    return target.entries();
  }

  if (typeof target === 'string') {
    return new URLSearchParams(target.startsWith('?') ? target.slice(1) : target).entries();
  }

  if (typeof target === 'object') {
    return Object.entries(target);
  }

  return null;
};

/** Parametreyi veya liste halinde verilen parametre adlarından ilk eşleşeni case-insensitive eşleşecek şekilde okur, ham değeri döner. */
const getParam = (source, name) => {
  const entries = toEntries(source);
  if (!entries) return null;

  const targetNames = (Array.isArray(name) ? name : [name]).map(normalize);

  for (const [key, value] of entries) {
    if (targetNames.includes(normalize(key))) return value;
  }

  return null;
};

/**
 * renderType değerini normalize edilmiş (trim + lowercase) olarak döner, yoksa null.
 *
 * @param {string|URLSearchParams|Object} [source]
 * @returns {string|null}
 */
export const getRenderType = source => normalize(getParam(source, RENDER_TYPE_PARAMS));

/**
 * URL'de `renderType=webview` veya `render-type=webview` var mı? (parametre adı ve değeri case-insensitive)
 *
 * @param {string|URLSearchParams|Object} [source]
 * @returns {boolean}
 */
export const isWebviewRenderType = source => (
  getRenderType(source) === RENDER_TYPE_WEBVIEW
);

/**
 * Elde hazır bir renderType değeri varken (prop, state, fonksiyon parametresi) kullanılır.
 *
 * @param {*} value
 * @returns {boolean}
 */
export const isWebviewRenderTypeValue = value => (
  normalize(value) === RENDER_TYPE_WEBVIEW
);

/**
 * renderType parametresi var ama `webview` değil (ör. `renderType=momentum` veya `render-type=momentum`).
 *
 * @param {string|URLSearchParams|Object} [source]
 * @returns {boolean}
 */
export const hasNonWebviewRenderType = source => {
  const renderType = getRenderType(source);
  return !!renderType && renderType !== RENDER_TYPE_WEBVIEW;
};

/**
 * URL'de dolu bir token parametresi var mı? (native app / deep link girişi)
 *
 * @param {string|URLSearchParams|Object} [source]
 * @returns {boolean}
 */
export const hasEmbeddedToken = source => EMBEDDED_TOKEN_QUERY_KEYS.some(key => {
  const value = getParam(source, key);
  return value != null && value !== '';
});

/**
 * Webview ya da URL'de token — SSR'ın light HTML döndüğü ve client bootstrap'ın
 * 3rd party'leri atladığı "embedded shell" durumu. SSR tarafında `req.query` ile
 * çağrılır, client tarafında `location.search` ile.
 *
 * @param {string|URLSearchParams|Object} [source]
 * @returns {boolean}
 */
export const isEmbeddedAppShell = source => (
  isWebviewRenderType(source) || hasEmbeddedToken(source)
);

/**
 * Sayfa/route seviyesinde UI kararları (header/footer tipi, geri butonu,
 * breadcrumb vb.) için kullanılacak "şu an webview modundayız" tespiti.
 * Önce anlık URL'e bakar; Lig Merkezi gibi SPA-içi navigasyonlarda query
 * string kaybolursa bootstrap'ta set edilen kalıcı flag'e
 * (`getWebviewModeFlag`) düşer, böylece webview modu bir kerelik URL
 * parametresine bağımlı kalmaz. Açık bir webview-olmayan renderType
 * (ör. `renderType=momentum`) varsa flag'i yok sayar.
 *
 * @param {string|URLSearchParams|Object} [source]
 * @returns {boolean}
 */
export const isWebviewMode = source => (
  isWebviewRenderType(source) || (getWebviewModeFlag() && !hasNonWebviewRenderType(source))
);

/**
 * Client bootstrap'ta (React hydrate öncesi) bir kez çağrılır; auth interceptor'ın
 * SPA navigasyonunda kullandığı kalıcı flag'i set eder.
 *
 * @returns {boolean}
 */
export function initEmbeddedShellFromWindow () {
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    const embedded = isEmbeddedAppShell(window.location.search);
    if (embedded) {
      setWebviewModeFlag(true);
    }
    return embedded;
  } catch (e) {
    return false;
  }
}
